import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Stack,
  Divider,
  Link,
  keyframes,
} from '@mui/material';

import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CodeIcon from '@mui/icons-material/Code';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import BoltIcon from '@mui/icons-material/Bolt';
import Logo from '../logo.png';

// Animation keyframes
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;


const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Platform',
      links: [
        { label: 'AI Engineering', icon: <BoltIcon sx={{ fontSize: 18 }} />, url: '/platform/ai-engineering' },
        { label: 'Cloud Native', icon: <CloudIcon sx={{ fontSize: 18 }} />, url: '/platform/cloud-native' },
        { label: 'Edge Computing', icon: <CodeIcon sx={{ fontSize: 18 }} />, url: '/platform/edge-computing' },
        { label: 'Cybersecurity', icon: <SecurityIcon sx={{ fontSize: 18 }} />, url: '/platform/cybersecurity' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', url: '/company/about' },
        { label: 'Careers', url: '/company/careers' },
        { label: 'Press', url: '/company/press' },
        { label: 'Contact', url: '/company/contact' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', url: '/legal/privacy' },
        { label: 'Terms of Service', url: '/legal/terms' },
        { label: 'Cookie Policy', url: '/legal/cookies' },
        { label: 'Security', url: '/legal/security' },
      ],
    },
  ];

  const socialLinks = [
    { icon: <GitHubIcon />, url: 'https://github.com/aediax', label: 'GitHub', color: '#333' },
    { icon: <LinkedInIcon />, url: 'https://linkedin.com/company/aediax', label: 'LinkedIn', color: '#0077b5' },
    { icon: <TwitterIcon />, url: 'https://twitter.com/aediax', label: 'Twitter', color: '#1da1f2' },
    { icon: <YouTubeIcon />, url: 'https://youtube.com/c/aediax', label: 'YouTube', color: '#ff0000' },
  ];

  // Geometric SVG Background Component
  const GeometricBackground = () => (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      {/* Main gradient overlay */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 800"
        preserveAspectRatio="none"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      >
        <defs>
          <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#000000', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#000000', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#000000', stopOpacity: 1 }} />
          </linearGradient>
          
          <radialGradient id="circleGrad1" cx="20%" cy="30%" r="50%">
            <stop offset="0%" style={{ stopColor: '#010213', stopOpacity: 0.15 }} />
            <stop offset="100%" style={{ stopColor: '#010515', stopOpacity: 0 }} />
          </radialGradient>
          
          <radialGradient id="circleGrad2" cx="80%" cy="70%" r="50%">
            <stop offset="0%" style={{ stopColor: '#10011e', stopOpacity: 0.12 }} />
            <stop offset="100%" style={{ stopColor: '#140324', stopOpacity: 0 }} />
          </radialGradient>

          <radialGradient id="circleGrad3" cx="50%" cy="50%" r="40%">
            <stop offset="0%" style={{ stopColor: '#04222f', stopOpacity: 0.08 }} />
            <stop offset="100%" style={{ stopColor: '#042635', stopOpacity: 0 }} />
          </radialGradient>

          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        <rect width="100%" height="100%" fill="url(#bgGrad)" />
        
        {/* Large geometric circles */}
        <circle cx="10%" cy="20%" r="300" fill="url(#circleGrad1)" />
        <circle cx="90%" cy="80%" r="350" fill="url(#circleGrad2)" />
        <circle cx="50%" cy="50%" r="250" fill="url(#circleGrad3)" />
        
        {/* Hexagon pattern */}
        <g opacity="0.03">
          <polygon points="100,50 150,80 150,140 100,170 50,140 50,80" fill="none" stroke="#dbe1ff" strokeWidth="2"/>
          <polygon points="300,150 350,180 350,240 300,270 250,240 250,180" fill="none" stroke="#eddaff" strokeWidth="2"/>
          <polygon points="500,80 550,110 550,170 500,200 450,170 450,110" fill="none" stroke="#a1e3ff" strokeWidth="2"/>
          <polygon points="700,200 750,230 750,290 700,320 650,290 650,230" fill="none" stroke="#667eea" strokeWidth="2"/>
          <polygon points="900,100 950,130 950,190 900,220 850,190 850,130" fill="none" stroke="#764ba2" strokeWidth="2"/>
          <polygon points="1100,250 1150,280 1150,340 1100,370 1050,340 1050,280" fill="none" stroke="#38bdf8" strokeWidth="2"/>
          <polygon points="1300,150 1350,180 1350,240 1300,270 1250,240 1250,180" fill="none" stroke="#667eea" strokeWidth="2"/>
        </g>

        {/* Animated rotating squares */}
        <g opacity="0.04">
          <rect x="200" y="400" width="60" height="60" fill="none" stroke="#c2cdff" strokeWidth="2">
            <animateTransform attributeName="transform" type="rotate" from="0 230 430" to="360 230 430" dur="20s" repeatCount="indefinite"/>
          </rect>
          <rect x="800" y="500" width="80" height="80" fill="none" stroke="#e4cbfe" strokeWidth="2">
            <animateTransform attributeName="transform" type="rotate" from="0 840 540" to="360 840 540" dur="25s" repeatCount="indefinite"/>
          </rect>
          <rect x="1200" y="300" width="50" height="50" fill="none" stroke="#b8e5f8" strokeWidth="2">
            <animateTransform attributeName="transform" type="rotate" from="0 1225 325" to="360 1225 325" dur="18s" repeatCount="indefinite"/>
          </rect>
        </g>

        {/* Connecting lines */}
        <g opacity="0.02" stroke="#667eea" strokeWidth="1">
          <line x1="200" y1="200" x2="600" y2="400" />
          <line x1="600" y1="400" x2="1000" y2="200" />
          <line x1="1000" y1="200" x2="1200" y2="500" />
          <line x1="200" y1="200" x2="400" y2="600" />
          <line x1="400" y1="600" x2="800" y2="700" />
          <line x1="800" y1="700" x2="1200" y2="500" />
        </g>
      </svg>

      {/* Floating particles */}
      {[...Array(12)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(102,126,234,0.6), rgba(118,75,162,0.3))`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `${float} ${Math.random() * 10 + 8}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
            filter: 'blur(2px)',
            zIndex: 0,
          }}
        />
      ))}
    </Box>
  );

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        color: '#E2E8F0',
        pt: { xs: 6, md: 10 },
        pb: 4,
        overflow: 'hidden',
      }}
    >
      <GeometricBackground />

      {/* Subtle shimmer line at top */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(102,126,234,0.6), rgba(118,75,162,0.6), rgba(56,189,248,0.6), transparent)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 4s linear infinite`,
          zIndex: 1,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Main footer content - Flexbox instead of Grid */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 4, md: 6 }, mb: 6 }}>
          {/* Brand column */}
          <Box sx={{ flex: '1 1 300px', minWidth: '280px' }}>
            <Box sx={{ maxWidth: 360 }}>
              <Box
                component={Link}
                href="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  textDecoration: 'none',
                  mb: 3,
                }}
              >
                <Box
                  component="img"
                  src={Logo}
                  alt="AediaX Logo"
                  sx={{
                    height: 44,
                    width: 'auto',
                    filter: 'brightness(1.1) drop-shadow(0 0 8px rgba(102,126,234,0.4))',
                    transition: '0.3s',
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #b8c5ff, #d1b0f3, #9be1ff)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.02em',
                    animation: `${shimmer} 3s linear infinite`,
                  }}
                >
                  AediaX Blog
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: '#94A3B8',
                  lineHeight: 1.7,
                  mb: 3,
                  fontSize: '0.9rem',
                }}
              >
                Engineering the future of digital intelligence through 
                cutting-edge AI systems, cloud-native architectures, 
                and scalable infrastructure.
              </Typography>

              {/* Social links */}
              <Stack direction="row" spacing={1.5}>
                {socialLinks.map((social) => (
                  <IconButton
                    key={social.label}
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#64748B',
                      bgcolor: 'rgba(15,23,42,0.6)',
                      border: '1px solid rgba(102,126,234,0.2)',
                      padding: 1.2,
                      transition: 'all 0.3s',
                      '&:hover': {
                        color: social.color,
                        bgcolor: 'rgba(102,126,234,0.1)',
                        borderColor: social.color,
                        transform: 'translateY(-3px)',
                      },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* Navigation columns - Flexbox layout */}
          {footerSections.map((section) => (
            <Box key={section.title} sx={{ flex: '1 1 180px', minWidth: '150px' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#CBD5E1',
                  mb: 2.5,
                  fontSize: '0.9rem',
                  letterSpacing: '0.5px',
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -6,
                    left: 0,
                    width: 28,
                    height: 2,
                    background: 'linear-gradient(90deg, #667eea, #764ba2)',
                    borderRadius: 2,
                  },
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1.5}>
                {section.links.map((link) => (
                  <Link
                    key={link.label}
                    href={link.url}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#7E8AA2',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: '#667eea',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    {link.icon && link.icon}
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}

          {/* CTA Column */}
          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: '#CBD5E1',
                mb: 2.5,
                fontSize: '0.9rem',
                letterSpacing: '0.5px',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -6,
                  left: 0,
                  width: 28,
                  height: 2,
                  background: 'linear-gradient(90deg, #667eea, #764ba2)',
                  borderRadius: 2,
                },
              }}
            >
              Get Started
            </Typography>
            <Stack spacing={2}>
              <Button
                href="/company/contact"
                endIcon={<ArrowForwardIcon sx={{ transition: '0.2s' }} />}
                sx={{
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 1,
                  borderRadius: '10px',
                  color: '#E2E8F0',
                  bgcolor: 'rgba(102,126,234,0.08)',
                  border: '1px solid rgba(102,126,234,0.2)',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'rgba(102,126,234,0.15)',
                    borderColor: 'rgba(102,126,234,0.5)',
                    transform: 'translateX(4px)',
                    '& .MuiButton-endIcon svg': {
                      transform: 'translateX(4px)',
                    },
                  },
                }}
              >
                Contact Sales
              </Button>
           
            </Stack>
          </Box>
        </Box>

        {/* Bottom section */}
        <Divider
          sx={{
            my: 4,
            borderColor: 'rgba(102,126,234,0.1)',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            pb: 2,
          }}
        >
          <Typography
            sx={{
              color: '#5B6A8A',
              fontSize: '0.8rem',
              textAlign: 'center',
            }}
          >
            © {currentYear} AediaX Blog. All rights reserved. 
            <Box
              component="span"
              sx={{
                display: { xs: 'block', sm: 'inline' },
                ml: { sm: 1 },
                color: '#667eea',
              }}
            >
              Innovate at scale.
            </Box>
          </Typography>

          <Stack direction="row" spacing={3}>
            <Link
              href="/legal/privacy"
              sx={{
                color: '#7E8AA2',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: '0.2s',
                '&:hover': {
                  color: '#667eea',
                },
              }}
            >
              Privacy
            </Link>
            <Link
              href="/legal/terms"
              sx={{
                color: '#7E8AA2',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: '0.2s',
                '&:hover': {
                  color: '#667eea',
                },
              }}
            >
              Terms
            </Link>
            <Link
              href="/legal/cookies"
              sx={{
                color: '#7E8AA2',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: '0.2s',
                '&:hover': {
                  color: '#667eea',
                },
              }}
            >
              Cookies
            </Link>
            <Link
              href="/sitemap.xml"
              sx={{
                color: '#7E8AA2',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: '0.2s',
                '&:hover': {
                  color: '#667eea',
                },
              }}
            >
              Sitemap
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;